// walletApiClient.js - Enhanced for both Google and Apple Wallet support
import axios from 'axios';

// Base API URL - Updated for correct backend port
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Platform detection utility
const detectPlatform = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Check if iOS
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios';
  }
  
  // Check if Android
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  
  // Default to android for web browsers (Google Wallet works on web)
  return 'android';
};

// Get wallet type based on platform
const getWalletType = (forcedType = null) => {
  if (forcedType) return forcedType;
  
  const platform = detectPlatform();
  return platform === 'ios' ? 'apple' : 'google';
};

/**
 * Generate a wallet pass (Google or Apple based on platform)
 * @param {object} profileData - The profile data
 * @param {object} options - Wallet pass options
 * @param {string} token - Authentication token
 * @param {string} walletType - Force specific wallet type ('google' or 'apple')
 * @returns {Promise} The generated pass data
 */
export const generateWalletPass = async (profileId, options = {}, token, walletType = null) => {
  try {
    const targetWallet = getWalletType(walletType);
    
    // Prepare different payloads for different wallet types
    let requestPayload;
    
    if (targetWallet === 'apple') {
      // Apple Wallet format
      requestPayload = {
        userId: profileId,
        email: options.email || '',
        firstName: options.firstName || '',
        lastName: options.lastName || '',
        passData: {
          passStyle: options.passType || 'generic',
          color: options.color || '#007AFF',
          logoText: options.logoText || 'KoorAI Pro',
          organizationName: options.organizationName || 'KoorAI Digital Solutions',
          description: options.description || 'Digital Profile Card',
          includedFields: options.includedFields || {},
          trackingEnabled: options.trackingEnabled || false
        }
      };
    } else {
      // Google Wallet format
      requestPayload = {
        passType: options.passType || 'generic',
        passData: {
          email: options.email || '',
          firstName: options.firstName || '',
          lastName: options.lastName || '',
          logoText: options.logoText || 'KoorAI Pro',
          organizationName: options.organizationName || 'KoorAI Digital Solutions',
          description: options.description || 'Digital Profile Card',
          includedFields: options.includedFields || {}
        },
        color: options.color || '#007AFF',
        trackingEnabled: options.trackingEnabled || false
      };
    }
    
    const response = await axios.post(
      `${API_URL}/api/wallets/${targetWallet}/generate-pass`,
      requestPayload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      success: true,
      walletType: targetWallet,
      platform: detectPlatform(),
      ...response.data
    };
  } catch (error) {
    console.error('Error generating wallet pass:', error);
    const errorMessage = error.response?.data?.message || error.message || 'Unknown error generating wallet pass';
    return {
      success: false,
      error: errorMessage,
      walletType: getWalletType(walletType),
      platform: detectPlatform()
    };
  }
};

/**
 * Get all wallet passes for a profile
 * @param {number} profileId - The profile ID
 * @param {string} token - Authentication token
 * @returns {Promise} List of passes
 */
export const getWalletPasses = async (profileId, token) => {
  try {
    const response = await axios.get(
      `${API_URL}/api/wallets/analytics/${profileId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error fetching wallet passes:', error);
    throw error.response?.data || { error: 'Unknown error fetching wallet passes' };
  }
};

/**
 * Send a wallet pass via email
 * @param {number} profileId - The profile ID
 * @param {string} email - Recipient email
 * @param {string} passId - Optional existing pass ID
 * @param {string} token - Authentication token
 * @param {string} walletType - Force specific wallet type ('google' or 'apple')
 * @returns {Promise} Email sending result
 */
export const emailWalletPass = async (profileId, email, passId, token, walletType = null) => {
  try {
    const targetWallet = getWalletType(walletType);
    
    const response = await axios.post(
      `${API_URL}/api/wallets/${targetWallet}/email-pass`,
      {
        userId: profileId,
        email,
        passId
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error emailing wallet pass:', error);
    throw error.response?.data || { error: 'Unknown error emailing wallet pass' };
  }
};

/**
 * Get wallet pass analytics
 * @param {number} profileId - The profile ID
 * @param {string} token - Authentication token
 * @returns {Promise} Analytics data
 */
export const getWalletAnalytics = async (profileId, token) => {
  try {
    const response = await axios.get(
      `${API_URL}/api/wallets/analytics/${profileId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error fetching wallet analytics:', error);
    throw error.response?.data || { error: 'Unknown error fetching wallet analytics' };
  }
};

/**
 * Check wallet service health
 * @returns {Promise} Health status
 */
export const checkWalletHealth = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/wallets/health`);
    return response.data;
  } catch (error) {
    console.error('Error checking wallet health:', error);
    throw error.response?.data || { error: 'Unknown error checking wallet health' };
  }
};

/**
 * Get platform-specific wallet information
 * @returns {object} Platform and wallet info
 */
export const getPlatformInfo = () => {
  const platform = detectPlatform();
  const walletType = getWalletType();
  
  return {
    platform,
    walletType,
    isIOS: platform === 'ios',
    isAndroid: platform === 'android',
    supportsAppleWallet: platform === 'ios',
    supportsGoogleWallet: true, // Google Wallet works on all platforms
    recommendedWallet: walletType
  };
};

export default {
  generateWalletPass,
  getWalletPasses,
  emailWalletPass,
  getWalletAnalytics,
  checkWalletHealth,
  getPlatformInfo,
  detectPlatform,
  getWalletType
};